import {
  Camera,
  CapsuleGeometry,
  Color,
  DoubleSide,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Object3D,
  Quaternion,
  Scene,
  ShaderMaterial,
  Vector3,
} from "three"
import { GLBModel } from "./GLBModel"

export type TubeWorldParticleStreamOptions = {
  maxParticles?: number
  spawnPerSecond?: number
  speedMin?: number
  speedMax?: number
  lifetimeMin?: number
  lifetimeMax?: number
  positionJitter?: number
  /** Полуапертурный угол конуса вокруг направления камеры (рад.) */
  directionConeHalfAngle?: number
  pointSize?: number
}

export type TubeWorldParticleStream = {
  update: (sourceMesh: Object3D, camera: Camera, emitting?: boolean) => void
  dispose: () => void
}

const tmpDir = new Vector3()
const tmpU = new Vector3()
const tmpV = new Vector3()
const tmpVel = new Vector3()
const tmpSpawn = new Vector3()
const tmpForward = new Vector3()
const upAxis = new Vector3(0, 1, 0)
const tmpQuat = new Quaternion()
const hiddenMatrix = new Matrix4().makeScale(0.0001, 0.0001, 0.0001)

/** Равномерная случайная единичная вектор внутри конуса вокруг `axis` (полуапертур `halfAngleRad`). */
function randomUnitInCone(axis: Vector3, halfAngleRad: number, out: Vector3): void {
  if (halfAngleRad < 1e-6) {
    out.copy(axis)
    return
  }
  if (Math.abs(axis.x) < 0.9) {
    tmpU.set(1, 0, 0)
  } else {
    tmpU.set(0, 1, 0)
  }
  tmpU.crossVectors(axis, tmpU)
  if (tmpU.lengthSq() < 1e-10) {
    tmpU.set(0, 0, 1).crossVectors(axis, tmpU)
  }
  tmpU.normalize()
  tmpV.crossVectors(axis, tmpU).normalize()

  const cosLimit = Math.cos(halfAngleRad)
  const cosTheta = 1 - Math.random() * (1 - cosLimit)
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta))
  const phi = Math.random() * Math.PI * 2

  out
    .copy(axis)
    .multiplyScalar(cosTheta)
    .addScaledVector(tmpU, sinTheta * Math.cos(phi))
    .addScaledVector(tmpV, sinTheta * Math.sin(phi))
    .normalize()
}

/**
 * Поток частиц в мировых координатах: скорость фиксируется при рождении по направлению камеры,
 * дальше не зависит от поворота камеры. Инстансы капсул лежат в корне сцены.
 */
export function createTubeWorldParticleStream(
  scene: Scene | Group,
  options: TubeWorldParticleStreamOptions = {},
): TubeWorldParticleStream {
  const maxParticles = options.maxParticles ?? 1000
  const spawnPerSecond = options.spawnPerSecond ?? 140
  const speedMin = options.speedMin ?? 3.2
  const speedMax = options.speedMax ?? 4.6
  const lifetimeMin = options.lifetimeMin ?? 0.75
  const lifetimeMax = options.lifetimeMax ?? 1.75
  const positionJitter = options.positionJitter ?? 0.04
  const directionConeHalfAngle = options.directionConeHalfAngle ?? 0.24
  const pointSize = options.pointSize ?? 0.32

  const lives = new Float32Array(maxParticles)
  const velocities = new Float32Array(maxParticles * 3)
  const timeLeft = new Float32Array(maxParticles)
  const maxLife = new Float32Array(maxParticles)
  const alive = new Uint8Array(maxParticles)
  const positions = new Float32Array(maxParticles * 3)
  const particleSizeVariants = new Float32Array(maxParticles)
  const particleColors = new Float32Array(maxParticles * 3).fill(1)
  const dummy = new Object3D()
  const colorPalette = [
    new Color("#7fcf8d"),
    new Color("#b8c86a"),
    new Color("#d5c86e"),
    new Color("#c99b5d"),
    new Color("#76bfa5"),
    new Color("#9fca7a"),
  ]
  const sizeVariants = [0.95, 1.15, 1.4]

  const geometry = new CapsuleGeometry(pointSize * 0.082, pointSize * 0.4, 4, 10)
  const particleColorAttribute = new InstancedBufferAttribute(particleColors, 3)
  geometry.setAttribute("particleColor", particleColorAttribute)

  const material = new ShaderMaterial({
    vertexShader: `
      attribute vec3 particleColor;
      varying vec3 vParticleColor;
      varying vec3 vLocalNormal;

      void main() {
        vParticleColor = particleColor;
        vLocalNormal = normal;

        vec4 transformed = vec4(position, 1.0);
        #ifdef USE_INSTANCING
          transformed = instanceMatrix * transformed;
        #endif

        gl_Position = projectionMatrix * modelViewMatrix * transformed;
      }
    `,
    fragmentShader: `
      varying vec3 vParticleColor;
      varying vec3 vLocalNormal;

      void main() {
        float sideLight = 0.82 + 0.22 * abs(normalize(vLocalNormal).x);
        vec3 color = vParticleColor * sideLight + vec3(0.025, 0.032, 0.012);
        gl_FragColor = vec4(color, 0.72);
      }
    `,
    transparent: true,
    opacity: 0.78,
    depthWrite: false,
    depthTest: true,
    fog: false,
    side: DoubleSide,
    toneMapped: false,
  })

  const particles = new InstancedMesh(geometry, material, maxParticles)
  particles.frustumCulled = false
  particles.matrixAutoUpdate = false
  particles.renderOrder = 1_000_190
  particles.updateMatrix()
  particles.layers.set(1);
  scene.add(particles)

  for (let i = 0; i < maxParticles; i++) {
    particles.setMatrixAt(i, hiddenMatrix)
  }
  particles.instanceMatrix.needsUpdate = true

  let spawnCarry = 0
  let lastNow = performance.now() / 1000
  let aliveCount = 0
  let maxUsedIndex = -1

  // Free-list: стек свободных индексов
  const freeList = new Int32Array(maxParticles)
  let freeCount = maxParticles
  for (let i = 0; i < maxParticles; i++) freeList[i] = maxParticles - 1 - i

  const spawnOne = (sourceMesh: Object3D, camera: Camera) => {
    if (freeCount === 0) return
    const idx = freeList[--freeCount]
    if (idx > maxUsedIndex) maxUsedIndex = idx

    sourceMesh.getWorldPosition(tmpSpawn)

    camera.getWorldDirection(tmpForward)
    tmpSpawn.addScaledVector(tmpForward, 0.62)

    const j = positionJitter
    tmpSpawn.x += (Math.random() * 2 - 1) * j
    tmpSpawn.y += (Math.random() * 2 - 1) * j
    tmpSpawn.z += (Math.random() * 2 - 1) * j

    randomUnitInCone(tmpForward, directionConeHalfAngle, tmpVel)
    const speed = speedMin + Math.random() * (speedMax - speedMin)
    tmpVel.multiplyScalar(speed)

    const i3 = idx * 3
    positions[i3] = tmpSpawn.x
    positions[i3 + 1] = tmpSpawn.y
    positions[i3 + 2] = tmpSpawn.z

    velocities[i3] = tmpVel.x
    velocities[i3 + 1] = tmpVel.y
    velocities[i3 + 2] = tmpVel.z

    const T = lifetimeMin + Math.random() * (lifetimeMax - lifetimeMin)
    timeLeft[idx] = T
    maxLife[idx] = T
    lives[idx] = 1
    alive[idx] = 1
    aliveCount++
    particleSizeVariants[idx] =
      sizeVariants[Math.floor(Math.random() * sizeVariants.length)]

    const randomColor = colorPalette[Math.floor(Math.random() * colorPalette.length)]
    randomColor.toArray(particleColors, idx * 3)
    particleColorAttribute.needsUpdate = true
  }

  return {
    update(sourceMesh: Object3D, camera: Camera, emitting = true) {
      const now = performance.now() / 1000
      const dt = Math.min(0.08, Math.max(0, now - lastNow))
      lastNow = now

      const loopEnd = maxUsedIndex + 1
      for (let i = 0; i < loopEnd; i++) {
        if (!alive[i]) {
          continue
        }
        const i3 = i * 3
        const t = timeLeft[i] - dt
        if (t <= 0) {
          alive[i] = 0
          aliveCount--
          freeList[freeCount++] = i
          if (i === maxUsedIndex) {
            while (maxUsedIndex >= 0 && !alive[maxUsedIndex]) maxUsedIndex--
          }
          lives[i] = 0
          particles.setMatrixAt(i, hiddenMatrix)
          continue
        }
        timeLeft[i] = t
        lives[i] = maxLife[i] > 1e-6 ? t / maxLife[i] : 0

        positions[i3] += velocities[i3] * dt
        positions[i3 + 1] += velocities[i3 + 1] * dt
        positions[i3 + 2] += velocities[i3 + 2] * dt

        tmpForward.set(velocities[i3], velocities[i3 + 1], velocities[i3 + 2]).normalize()
        tmpQuat.setFromUnitVectors(upAxis, tmpForward)
        const lifeScale = (0.6 + lives[i] * 0.75) * particleSizeVariants[i]

        dummy.position.set(positions[i3], positions[i3 + 1], positions[i3 + 2])
        dummy.quaternion.copy(tmpQuat)
        dummy.scale.set(lifeScale * 0.38, lifeScale * 1.28, lifeScale * 0.38)
        dummy.updateMatrix()
        particles.setMatrixAt(i, dummy.matrix)
      }

      if (emitting) {
        spawnCarry += spawnPerSecond * dt
        let toSpawn = Math.floor(spawnCarry)
        spawnCarry -= toSpawn
        toSpawn = Math.min(toSpawn, 17)
        for (let s = 0; s < toSpawn; s++) {
          spawnOne(sourceMesh, camera)
        }
      } else {
        spawnCarry = 0
      }

      particles.instanceMatrix.needsUpdate = true
    },

    dispose() {
      scene.remove(particles)
      geometry.dispose()
      material.dispose()
    },
  }
}

export class TubeBacteriesModel extends GLBModel {
  constructor() {
    super("/models/weapon/tube.glb")
  }

  get key(): string {
    return "weapon_tube"
  }

  /** Поток частиц в мировом пространстве (см. `createTubeWorldParticleStream`). */
  createWorldParticleStream(
    scene: Scene | Group,
    options?: TubeWorldParticleStreamOptions,
  ): TubeWorldParticleStream {
    return createTubeWorldParticleStream(scene, options)
  }

  getModel(): Object3D | null {
    const mesh = super.getModel()

    if (!mesh) {
      return null
    }

    mesh.traverse((child) => {
      child.layers.set(1);
    });

    return mesh
  }
}
